import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Trash2, Calendar, User, CalendarClock, Plus, History, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import BrandLogo from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { visibleFollowups } from "@/components/QuestionInput";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FU_LABEL = {
  non_previsto: "Follow-up non ancora previsto",
  in_arrivo: "Follow-up in arrivo",
  disponibile: "Follow-up disponibile",
  in_ritardo: "Follow-up in ritardo",
};
const PERCORSO_SERVICES = ["Personal Training", "Pilates Reformer", "Nutrizionista", "Massoterapia", "Recovery Room"];
const FOTO_STATI = [["verde", "🟢 Buono"], ["giallo", "🟡 Attenzione"], ["rosso", "🔴 Priorità"]];
const MAPPA_STATI = [["prioritaria", "Priorità"], ["da_costruire", "Da costruire"], ["in_evoluzione", "In evoluzione"], ["buona_base", "Buona base"]];
const BLOOD_STATI = [["verde", "🟢 Buono"], ["giallo", "🟡 Da migliorare"], ["rosso", "🔴 Fuori range"]];
const LIVELLI = [["fortemente_consigliato", "Fortemente consigliato"], ["consigliato", "Consigliato"]];
const RUOLI = [["", "— Ruolo (facoltativo)"], ["percorso_principale", "Percorso principale"], ["integrazione", "Integrazione"], ["recupero", "Recupero"]];
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
}

function formatAnswer(q, v) {
  if (v === undefined || v === "" || v === null) return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v) + (q.unit ? ` ${q.unit}` : "");
}

function isLegacyReport(c) {
  if (!c || typeof c !== "object") return false;
  const hasNew = c.punto_partenza !== undefined
    || Array.isArray(c.dove_intervenire)
    || Array.isArray(c.analisi_sangue)
    || (c.percorso && !Array.isArray(c.percorso) && typeof c.percorso === "object");
  const hasOld = c.profilo_sintesi !== undefined
    || (c.aree && typeof c.aree === "object")
    || Array.isArray(c.percorso)
    || (c.analisi_sangue && !Array.isArray(c.analisi_sangue) && typeof c.analisi_sangue === "object");
  return hasOld && !hasNew;
}
export default function ClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [schema, setSchema] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [confirm1, setConfirm1] = useState(false);
  const [confirm2, setConfirm2] = useState(false);
  const [notes, setNotes] = useState("");
  const [principale, setPrincipale] = useState("Lascia decidere al sistema");
  const [compl, setCompl] = useState([]);
  const [indicazioni, setIndicazioni] = useState([]);
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState("");
  const [instr, setInstr] = useState("");
  const [checks, setChecks] = useState([]);
  const [followup, setFollowup] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [fuConfirm, setFuConfirm] = useState(false);
  const [fuLink, setFuLink] = useState("");
  const [delClient1, setDelClient1] = useState(false);

  const loadChecks = async () => {
    try {
      const { data } = await api.get(`/staff/submissions/${id}/checks`);
      setChecks(data.checks || []);
      setFollowup(data.followup || null);
      setClientId(data.client_id || null);
    } catch (e) { /* noop */ }
  };

  const reload = async () => {
    const { data } = await api.get(`/staff/submissions/${id}`);
    setDoc(data);
    const r = data.report || {};
    setNotes(r.staff_notes || "");
    const ps = r.path_selection || {};
    setPrincipale(ps.principale || "Lascia decidere al sistema");
    setCompl(ps.complementari || []);
    setIndicazioni(ps.indicazioni || []);
    setContent(r.content || null);
    loadChecks();
  };

  const call = async (label, fn) => {
    setBusy(label);
    try { await fn(); await reload(); toast.success("Fatto."); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita."); }
    finally { setBusy(""); }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind); fd.append("file", file);
    await call("upload", () => api.post(`/staff/submissions/${id}/files`, fd));
  };
  const saveSettings = () => call("settings", () => api.patch(`/staff/submissions/${id}/report/settings`, { staff_notes: notes, path_selection: { principale, complementari: compl, indicazioni } }));
  const genReport = () => call("gen", () => api.post(`/staff/submissions/${id}/report/generate`));
  const applyChanges = () => call("apply", async () => { await api.patch(`/staff/submissions/${id}/report/settings`, { staff_notes: notes, path_selection: { principale, complementari: compl, indicazioni } }); await api.post(`/staff/submissions/${id}/report/apply-changes`, { instructions: instr }); setInstr(""); });
  const regenPath = () => call("path", async () => { await api.patch(`/staff/submissions/${id}/report/settings`, { path_selection: { principale, complementari: compl, indicazioni } }); await api.post(`/staff/submissions/${id}/report/regenerate-path`); });
  const saveManual = () => call("save", () => api.patch(`/staff/submissions/${id}/report`, { content }));
  const approve = () => call("approve", () => api.post(`/staff/submissions/${id}/report/approve`));
  const exportReportPdf = async () => {
    try {
      const res = await api.get(`/staff/submissions/${id}/report/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "LongevityReport.pdf"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export PDF non riuscito."); }
  };
  const setField = (k, v) => setContent((c) => ({ ...(c || {}), [k]: v }));
  const setArr = (key, i, f, v) => setContent((c) => { const base = c || {}; const arr = Array.isArray(base[key]) ? [...base[key]] : []; arr[i] = { ...(arr[i] || {}), [f]: v }; return { ...base, [key]: arr }; });
  const addItem = (key, item, max) => setContent((c) => { const base = c || {}; const arr = Array.isArray(base[key]) ? [...base[key]] : []; if (arr.length >= max) return base; arr.push(item); return { ...base, [key]: arr }; });
  const removeItem = (key, i) => setContent((c) => { const base = c || {}; const arr = Array.isArray(base[key]) ? [...base[key]] : []; arr.splice(i, 1); return { ...base, [key]: arr }; });
  const addStr = (key, max) => setContent((c) => { const base = c || {}; const arr = Array.isArray(base[key]) ? [...base[key]] : []; if (arr.length >= max) return base; arr.push(""); return { ...base, [key]: arr }; });
  const setStrArr = (key, i, v) => setContent((c) => { const base = c || {}; const arr = Array.isArray(base[key]) ? [...base[key]] : []; arr[i] = v; return { ...base, [key]: arr }; });
  const setIndic = (i, f, v) => setIndicazioni((arr) => { const n = [...arr]; n[i] = { ...(n[i] || {}), [f]: v }; return n; });
  const addIndic = () => setIndicazioni((arr) => arr.length >= 4 ? arr : [...arr, { servizio: "Personal Training", livello: "fortemente_consigliato", frequenza: "", ruolo: "", motivo: "" }]);
  const removeIndic = (i) => setIndicazioni((arr) => arr.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (!doc) return;
    const r = doc.report || {};
    setNotes(r.staff_notes || "");
    const ps = r.path_selection || {};
    setPrincipale(ps.principale || "Lascia decidere al sistema");
    setCompl(ps.complementari || []);
    setIndicazioni(ps.indicazioni || []);
    setContent(r.content || null);
  }, [doc]);

  useEffect(() => {
    Promise.all([
      api.get(`/staff/submissions/${id}`),
      api.get(`/questionnaire`),
      api.get(`/staff/submissions/${id}/checks`),
    ]).then(([d, s, ch]) => {
      setDoc(d.data); setSchema(s.data);
      setChecks(ch.data.checks || []); setFollowup(ch.data.followup || null); setClientId(ch.data.client_id || null);
    })
      .catch(() => { toast.error("Scheda non trovata."); navigate("/staff/dashboard", { replace: true }); });
  }, [id, navigate]);

  const doStartFollowup = async () => {
    try {
      const { data } = await api.post(`/staff/submissions/${id}/followup`);
      setFuLink(`${window.location.origin}/c/${data.id}`);
    } catch (e) { toast.error("Impossibile avviare il follow-up."); }
  };
  const onAvviaFollowup = () => {
    if (followup && followup.state === "non_previsto") { setFuConfirm(true); return; }
    doStartFollowup();
  };
  const handleDeleteClient = async () => {
    try {
      await api.delete(`/staff/clients/${clientId}`);
      toast.success("Cliente e storico eliminati.");
      navigate("/staff/dashboard", { replace: true });
    } catch (e) { toast.error("Eliminazione non riuscita."); }
  };

  const handlePdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(`/staff/submissions/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const name = `${doc?.client?.last_name || ""}_${doc?.client?.first_name || "LongevityTest"}`.replace(/[^a-zA-Z0-9_-]/g, "") || "LongevityTest";
      a.download = `LongevityTest_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Esportazione PDF non riuscita.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/staff/submissions/${id}`);
      toast.success("Questionario eliminato.");
      navigate("/staff/dashboard", { replace: true });
    } catch (e) {
      toast.error("Eliminazione non riuscita.");
    }
  };

  if (!doc || !schema) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-foreground/20 border-t-foreground animate-spin" />
      </div>
    );
  }

  const c = doc.client || {};
  const answers = doc.answers || {};
  const fullName = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—";
  const checkLabel = doc.check_type === "follow-up" ? `Follow-up ${(doc.sequence || 2) - 1}` : "Baseline";
  const legacyReport = content ? isLegacyReport(content) : false;

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="fs-dark text-white fs-no-print">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/staff/dashboard")} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
          <BrandLogo variant="light" className="h-6" />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          {/* Header card */}
          <div className="fs-card p-6 sm:p-7">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
              <div>
                <p className="fs-eyebrow">Longevity Test · {checkLabel}</p>
                <h1 className="mt-2 font-heading text-3xl font-700 tracking-tight" data-testid="client-name">{fullName}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(doc.submitted_at || doc.created_at)}</span>
                  {c.birth_date && <span className="flex items-center gap-1.5"><User className="h-4 w-4" />Nato/a il {c.birth_date}</span>}
                  <Badge variant={doc.status === "completed" ? "default" : "secondary"} className="rounded-full">
                    {doc.status === "completed" ? "Completato" : "Bozza"}
                  </Badge>
                </div>
                {(c.email || c.phone || c.gender) && (
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                    {c.gender && <span>{c.gender}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    {c.email && <span>{c.email}</span>}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2.5 fs-no-print shrink-0">
                <Button onClick={handlePdf} disabled={downloading} data-testid="export-pdf-btn" className="h-11 rounded-full font-semibold">
                  <Download className="mr-2 h-4 w-4" /> {downloading ? "…" : "Esporta PDF"}
                </Button>
                <AlertDialog open={confirm1} onOpenChange={setConfirm1}>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" data-testid="delete-btn" className="h-11 w-11 rounded-full p-0 border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare questa valutazione?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Stai per eliminare questa valutazione ({checkLabel}) di {fullName}. Gli altri
                        Longevity Check dello stesso cliente non verranno toccati. Vuoi continuare?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="delete-confirm"
                        onClick={(e) => { e.preventDefault(); setConfirm1(false); setTimeout(() => setConfirm2(true), 60); }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Continua
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog open={confirm2} onOpenChange={setConfirm2}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Conferma definitiva</AlertDialogTitle>
                      <AlertDialogDescription>
                        Questa azione è <b>irreversibile</b>: questa valutazione ({checkLabel}) di {fullName}
                        e i relativi file (BIA, analisi, report) verranno eliminati definitivamente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel-final">Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="delete-confirm-final"
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Elimina definitivamente
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          {/* PROSSIMO LONGEVITY CHECK */}
          <section className="fs-card p-6 mt-6" data-testid="prossimo-check">
            <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-muted-foreground" /><h2 className="fs-eyebrow">Prossimo Longevity Check</h2></div>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                {followup?.next_date ? (
                  <>
                    <p className="text-lg font-semibold" data-testid="next-followup-date">{new Date(followup.next_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</p>
                    <p className="text-sm text-muted-foreground">{followup.days > 0 ? `Follow-up tra ${followup.days} giorni` : (followup.days === 0 ? "Follow-up previsto oggi" : `In ritardo di ${Math.abs(followup.days)} giorni`)}</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Nessun check completato: la scadenza sarà calcolata dopo il primo invio.</p>
                )}
                <Badge className="mt-2 rounded-full" variant={followup?.state === "in_ritardo" ? "destructive" : (followup?.state === "non_previsto" ? "secondary" : "default")} data-testid="followup-state">{FU_LABEL[followup?.state] || FU_LABEL.non_previsto}</Badge>
              </div>
              <Button onClick={onAvviaFollowup} data-testid="avvia-followup" className="h-11 rounded-full shrink-0"><Plus className="mr-2 h-4 w-4" />Avvia Follow-up Longevity Check</Button>
            </div>
          </section>

          {/* STORICO LONGEVITY CHECK */}
          <section className="fs-card p-6 mt-6" data-testid="storico-check">
            <div className="flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /><h2 className="fs-eyebrow">Storico Longevity Check</h2></div>
            <ul className="mt-4 flex flex-col gap-2">
              {checks.map((ch) => (
                <li key={ch.id} data-testid={`check-row-${ch.id}`} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 ${ch.id === id ? "border-primary bg-secondary/50" : "border-border/60"}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{ch.check_type === "follow-up" ? `Follow-up ${ch.sequence - 1}` : "Baseline"}</span>
                      <Badge variant={ch.status === "completed" ? "default" : "secondary"} className="rounded-full text-[10px]">{ch.status === "completed" ? "Completato" : "Bozza"}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(ch.submitted_at || ch.created_at)}</p>
                    <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
                      <span>{ch.has_bia ? "BIA ✓" : "BIA —"}</span>
                      <span>{ch.has_blood ? "Analisi ✓" : "Analisi —"}</span>
                      <span>{ch.has_report ? "Report ✓" : "Report —"}</span>
                    </div>
                  </div>
                  {ch.id === id ? (
                    <span className="text-xs text-muted-foreground">Aperto</span>
                  ) : (
                    <button onClick={() => navigate(`/staff/cliente/${ch.id}`)} data-testid={`open-check-${ch.id}`} className="text-sm font-medium text-foreground/70 hover:text-foreground">Apri</button>
                  )}
                </li>
              ))}
            </ul>
            {clientId && (
              <div className="mt-4 pt-4 border-t border-border/60">
                <AlertDialog open={delClient1} onOpenChange={setDelClient1}>
                  <AlertDialogTrigger asChild>
                    <button data-testid="delete-client-btn" className="text-xs text-destructive hover:underline">Elimina cliente e tutto lo storico</button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare l'intero cliente?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Questa azione è <b>irreversibile</b>: verranno eliminati TUTTI i Longevity Check di
                        {" "}{fullName} ({checks.length}) e tutti i file associati (BIA, analisi, report).
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-client-cancel">Annulla</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-client-confirm" onClick={handleDeleteClient} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina tutto</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </section>

          {/* Follow-up: early-start confirm */}
          <AlertDialog open={fuConfirm} onOpenChange={setFuConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Avviare il Follow-up in anticipo?</AlertDialogTitle>
                <AlertDialogDescription>
                  Il Follow-up è previsto indicativamente a 6 mesi dall'ultimo Longevity Check. Vuoi procedere comunque?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="fu-confirm-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="fu-confirm-proceed" onClick={() => { setFuConfirm(false); doStartFollowup(); }}>Procedi</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Follow-up: share link */}
          <AlertDialog open={!!fuLink} onOpenChange={(o) => { if (!o) { setFuLink(""); reload(); } }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Follow-up avviato</AlertDialogTitle>
                <AlertDialogDescription>
                  Condividi questo link con il cliente (es. WhatsApp) oppure apri subito il questionario su questo
                  dispositivo. Il cliente ricompilerà da zero il Longevity Test.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2">
                <input readOnly value={fuLink} data-testid="fu-link" className="flex-1 bg-transparent text-xs outline-none" />
                <button onClick={() => { navigator.clipboard?.writeText(fuLink); toast.success("Link copiato."); }} data-testid="fu-link-copy" className="text-foreground/70 hover:text-foreground"><Copy className="h-4 w-4" /></button>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="fu-link-close" onClick={() => { setFuLink(""); reload(); }}>Chiudi</AlertDialogCancel>
                <AlertDialogAction data-testid="fu-link-open" onClick={() => navigate(`/c/${fuLink.split("/c/")[1]}`)}><ExternalLink className="mr-2 h-4 w-4" />Apri questionario</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Consenso privacy (prova, consultabile dallo staff) */}
          <section className="fs-card p-6 mt-6" data-testid="consent-proof">
            <h2 className="fs-eyebrow">Consenso privacy</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-1">
              <div className="py-3 border-b border-border/60">
                <p className="text-xs text-muted-foreground leading-snug">Presa visione Informativa Privacy</p>
                <p className="mt-1 text-base font-medium">{doc.consents?.privacy_accepted ? "Sì" : "No"}</p>
              </div>
              <div className="py-3 border-b border-border/60">
                <p className="text-xs text-muted-foreground leading-snug">Consenso dati relativi alla salute</p>
                <p className="mt-1 text-base font-medium">{doc.consents?.consent_accepted ? "Sì" : "No"}</p>
              </div>
              <div className="py-3 border-b border-border/60">
                <p className="text-xs text-muted-foreground leading-snug">Versione Informativa</p>
                <p className="mt-1 text-base font-medium">{doc.consents?.privacy_version || "—"}</p>
              </div>
              <div className="py-3 border-b border-border/60">
                <p className="text-xs text-muted-foreground leading-snug">Versione Consenso</p>
                <p className="mt-1 text-base font-medium">{doc.consents?.consent_version || "—"}</p>
              </div>
              <div className="py-3 border-b border-border/60 sm:col-span-2">
                <p className="text-xs text-muted-foreground leading-snug">Data e ora del consenso</p>
                <p className="mt-1 text-base font-medium">{formatDate(doc.consents?.accepted_at)}</p>
              </div>
            </div>
          </section>

          {/* Answers grouped by section */}
          <div className="mt-6 flex flex-col gap-5">
            {schema.sections.map((section) => {
              const qs = section.questions.filter((q) => q.id in answers);
              if (qs.length === 0) return null;
              return (
                <section key={section.id} className="fs-card p-6" data-testid={`section-${section.id}`}>
                  <h2 className="fs-eyebrow">{section.title}</h2>
                  <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                    {qs.map((q) => (
                      <div key={q.id} className="py-3 border-b border-border/60">
                        <p className="text-xs text-muted-foreground leading-snug">{q.label}</p>
                        <p className="mt-1 text-base font-medium break-words">{formatAnswer(q, answers[q.id])}</p>
                        {visibleFollowups(q, answers).map((f) => (
                          answers[f.id] !== undefined && (
                            <div key={f.id} className="mt-2 pl-3 border-l-2 border-border">
                              <p className="text-xs text-muted-foreground leading-snug">{f.label}</p>
                              <p className="mt-0.5 text-base font-medium break-words">{formatAnswer(f, answers[f.id])}</p>
                            </div>
                          )
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
          {/* ===== LONGEVITY CHECK (staff) ===== */}
          <section className="fs-card p-6 mt-6" data-testid="longevity-check">
            <h2 className="fs-eyebrow">Longevity Check</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold">BIA (PDF)</Label>
                <input type="file" accept="application/pdf" data-testid="upload-bia" className="mt-1 block w-full text-sm" onChange={(e) => uploadFile("bia", e.target.files[0])} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Analisi del sangue (PDF, facoltative)</Label>
                <input type="file" accept="application/pdf" data-testid="upload-blood" className="mt-1 block w-full text-sm" onChange={(e) => uploadFile("blood", e.target.files[0])} />
              </div>
            </div>
            {(doc.files || []).length > 0 && (
              <ul className="mt-3 flex flex-col gap-1.5">
                {(doc.files || []).map((f) => (
                  <li key={f.id} className="flex items-center justify-between text-sm border border-border/60 rounded-lg px-3 py-2">
                    <span className="truncate">{f.kind === "bia" ? "BIA" : "Analisi"} · {f.filename}{f.text_extracted === false ? " (testo non estratto)" : ""}</span>
                    <div className="flex gap-3 shrink-0">
                      <a href={`${API}/staff/submissions/${id}/files/${f.id}`} target="_blank" rel="noreferrer" className="text-foreground/70 hover:text-foreground">Apri</a>
                      <button data-testid={`del-file-${f.id}`} onClick={() => call("delfile", () => api.delete(`/staff/submissions/${id}/files/${f.id}`))} className="text-destructive">Elimina</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4">
              <Label className="text-xs font-semibold">Note dello staff</Label>
              <Textarea data-testid="staff-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-xl min-h-[90px]" placeholder="Informazioni emerse dal colloquio…" />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Indicazioni del team (vincolanti)</Label>
                {indicazioni.length < 4 && (
                  <button type="button" data-testid="add-staff-indicazione" onClick={addIndic} className="text-xs text-primary hover:underline">+ Aggiungi indicazione</button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Ciò che inserisci qui prevale sull'AI: servizio, livello e frequenza vengono riportati esattamente nel report. Lascia il motivo vuoto per farlo scrivere all'AI.</p>
              {indicazioni.length === 0 && <p className="text-[11px] text-muted-foreground mt-2">Nessuna indicazione manuale: l'AI proporrà il percorso in autonomia.</p>}
              {indicazioni.map((x, i) => (
                <div key={i} className="mt-2 rounded-xl border border-border/60 p-3" data-testid={`staff-indicazione-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={x.servizio || ""} onChange={(e) => setIndic(i, "servizio", e.target.value)} data-testid={`staff-indicazione-${i}-servizio`} className="h-9 rounded-xl border border-border bg-card px-2 text-sm">
                      {PERCORSO_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={x.livello || "fortemente_consigliato"} onChange={(e) => setIndic(i, "livello", e.target.value)} data-testid={`staff-indicazione-${i}-livello`} className="h-9 rounded-xl border border-border bg-card px-2 text-sm">
                      {LIVELLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={x.ruolo || ""} onChange={(e) => setIndic(i, "ruolo", e.target.value)} data-testid={`staff-indicazione-${i}-ruolo`} className="h-9 rounded-xl border border-border bg-card px-2 text-sm">
                      {RUOLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button type="button" onClick={() => removeIndic(i)} className="text-destructive text-xs ml-auto" data-testid={`staff-indicazione-${i}-remove`}>✕</button>
                  </div>
                  <Input value={x.frequenza || ""} onChange={(e) => setIndic(i, "frequenza", e.target.value)} className="mt-2 rounded-xl h-9" placeholder="Frequenza / modalità (es. 2 sessioni a settimana)" data-testid={`staff-indicazione-${i}-frequenza`} />
                  <Textarea value={x.motivo || ""} onChange={(e) => setIndic(i, "motivo", e.target.value)} className="mt-2 rounded-xl min-h-[38px]" placeholder="Motivazione (facoltativa; se vuota la scrive l'AI)" />
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button data-testid="gen-report" onClick={genReport} disabled={!!busy} className="h-11 rounded-full">{busy === "gen" ? "Genero…" : (content ? "Rigenera report completo" : "Genera Longevity Report")}</Button>
              <Button variant="outline" onClick={saveSettings} disabled={!!busy} className="h-11 rounded-full">Salva note/percorso</Button>
              <Button variant="outline" onClick={regenPath} disabled={!!busy || !content} className="h-11 rounded-full">Rigenera indicazioni</Button>
              {content && <Button variant="outline" onClick={exportReportPdf} data-testid="export-report-pdf" className="h-11 rounded-full"><Download className="mr-2 h-4 w-4" />Esporta PDF</Button>}
              {content && <Button onClick={approve} disabled={!!busy} data-testid="approve-report" className="h-11 rounded-full">Approva</Button>}
            </div>
            {doc.report?.status && <p className="mt-2 text-xs text-muted-foreground">Stato: <b>{doc.report.status === "approved" ? "Approvato" : "Bozza"}</b>{doc.report.generated_at ? ` · generato ${formatDate(doc.report.generated_at)}` : ""}{doc.report.updated_at ? ` · modificato ${formatDate(doc.report.updated_at)}` : ""}</p>}
            {content?.warning_staff && <p className="mt-2 text-sm text-destructive" data-testid="report-warning">⚠ {content.warning_staff}</p>}
            {content && (
              <div className="mt-5 border-t border-border/70 pt-5" data-testid="report-preview">
                <p className="fs-eyebrow text-[10px]">Anteprima Longevity Report (modificabile)</p>
                {content.bia_warning && <p className="mt-2 text-sm text-amber-600" data-testid="bia-warning">⚠ {content.bia_warning}</p>}

                {legacyReport ? (
                  <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="legacy-report-notice">
                    <p className="text-sm font-semibold text-amber-800">Report in formato precedente</p>
                    <p className="mt-1 text-sm text-amber-700">Questo report usa il vecchio formato e non è modificabile qui campo per campo. Puoi esportarlo in PDF così com'è, oppure premere “Rigenera report completo” per ottenerlo nel nuovo formato editabile.</p>
                    <div className="mt-3 space-y-2 text-sm">
                      {content.profilo_sintesi && <p className="whitespace-pre-wrap"><b>Sintesi:</b> {content.profilo_sintesi}</p>}
                      {content.prossimo_passo && <p className="whitespace-pre-wrap"><b>Prossimo passo:</b> {content.prossimo_passo}</p>}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mt-3">
                      <Label className="text-xs font-semibold">Il tuo punto di partenza</Label>
                      <Textarea data-testid="field-punto-partenza" value={content.punto_partenza || ""} onChange={(e) => setField("punto_partenza", e.target.value)} className="mt-1 rounded-xl min-h-[70px]" placeholder="Consapevolezza del punto di partenza (max 2 righe)" />
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">La tua fotografia oggi (3-4 indicatori)</p>
                      {(Array.isArray(content.fotografia) ? content.fotografia.length : 0) < 4 && (
                        <button type="button" data-testid="add-fotografia" onClick={() => addItem("fotografia", { etichetta: "", valore: "", tag: "", stato: "giallo" }, 4)} className="text-xs text-primary hover:underline">+ Aggiungi indicatore</button>
                      )}
                    </div>
                    {(Array.isArray(content.fotografia) ? content.fotografia : []).map((f, i) => (
                      <div key={i} className="mt-2 rounded-xl border border-border/60 p-3" data-testid={`fotografia-${i}`}>
                        <div className="flex items-center gap-2">
                          <Input value={f.valore || ""} onChange={(e) => setArr("fotografia", i, "valore", e.target.value)} className="rounded-xl h-9 w-28" placeholder="Valore" data-testid={`fotografia-${i}-valore`} />
                          <Input value={f.etichetta || ""} onChange={(e) => setArr("fotografia", i, "etichetta", e.target.value)} className="rounded-xl h-9 flex-1" placeholder="Etichetta" />
                          <select value={f.stato || "giallo"} onChange={(e) => setArr("fotografia", i, "stato", e.target.value)} data-testid={`fotografia-${i}-stato`} className="text-xs rounded-md border border-border px-2 py-1 bg-background">
                            {FOTO_STATI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          <button type="button" onClick={() => removeItem("fotografia", i)} className="text-destructive text-xs" data-testid={`fotografia-${i}-remove`}>✕</button>
                        </div>
                        <Input value={f.tag || ""} onChange={(e) => setArr("fotografia", i, "tag", e.target.value)} className="rounded-xl h-9 mt-2" placeholder="Tag (es. AREA DI MIGLIORAMENTO)" />
                      </div>
                    ))}

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">La tua mappa longevity (3-5 dimensioni)</p>
                      {(Array.isArray(content.mappa) ? content.mappa.length : 0) < 5 && (
                        <button type="button" data-testid="add-mappa" onClick={() => addItem("mappa", { dimensione: "", stato: "da_costruire" }, 5)} className="text-xs text-primary hover:underline">+ Aggiungi dimensione</button>
                      )}
                    </div>
                    {(Array.isArray(content.mappa) ? content.mappa : []).map((m, i) => (
                      <div key={i} className="mt-2 rounded-xl border border-border/60 p-3 flex items-center gap-2" data-testid={`mappa-${i}`}>
                        <input value={m.dimensione || ""} onChange={(e) => setArr("mappa", i, "dimensione", e.target.value)} className="flex-1 text-sm font-semibold bg-transparent outline-none" placeholder={`Dimensione ${i + 1}`} data-testid={`mappa-${i}-dimensione`} />
                        <select value={m.stato || "da_costruire"} onChange={(e) => setArr("mappa", i, "stato", e.target.value)} data-testid={`mappa-${i}-stato`} className="text-xs rounded-md border border-border px-2 py-1 bg-background">
                          {MAPPA_STATI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <button type="button" onClick={() => removeItem("mappa", i)} className="text-destructive text-xs" data-testid={`mappa-${i}-remove`}>✕</button>
                      </div>
                    ))}

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">Messaggi (2-3, emozionali)</p>
                      {(Array.isArray(content.messaggi) ? content.messaggi.length : 0) < 3 && (
                        <button type="button" data-testid="add-messaggio" onClick={() => addStr("messaggi", 3)} className="text-xs text-primary hover:underline">+ Aggiungi messaggio</button>
                      )}
                    </div>
                    {(Array.isArray(content.messaggi) ? content.messaggi : []).map((m, i) => (
                      <div key={i} className="mt-2 flex items-start gap-2" data-testid={`messaggio-${i}`}>
                        <Textarea value={m || ""} onChange={(e) => setStrArr("messaggi", i, e.target.value)} className="flex-1 rounded-xl min-h-[38px]" placeholder="Frase breve e forte sul cambiamento" />
                        <button type="button" onClick={() => removeItem("messaggi", i)} className="text-destructive text-xs mt-2" data-testid={`messaggio-${i}-remove`}>✕</button>
                      </div>
                    ))}

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">Dove possiamo portarti (3-5)</p>
                      {(Array.isArray(content.direzione) ? content.direzione.length : 0) < 5 && (
                        <button type="button" data-testid="add-direzione" onClick={() => addItem("direzione", { titolo: "", beneficio: "" }, 5)} className="text-xs text-primary hover:underline">+ Aggiungi</button>
                      )}
                    </div>
                    {(Array.isArray(content.direzione) ? content.direzione : []).map((d, i) => (
                      <div key={i} className="mt-2 rounded-xl border border-border/60 p-3 flex items-center gap-2" data-testid={`direzione-${i}`}>
                        <input value={d.titolo || ""} onChange={(e) => setArr("direzione", i, "titolo", e.target.value)} className="w-40 text-sm font-semibold bg-transparent outline-none" placeholder="PIÙ FORZA" data-testid={`direzione-${i}-titolo`} />
                        <input value={d.beneficio || ""} onChange={(e) => setArr("direzione", i, "beneficio", e.target.value)} className="flex-1 text-sm bg-transparent outline-none" placeholder="sentirti fisicamente più capace" />
                        <button type="button" onClick={() => removeItem("direzione", i)} className="text-destructive text-xs" data-testid={`direzione-${i}-remove`}>✕</button>
                      </div>
                    ))}

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">Le indicazioni del team (il fulcro del report)</p>
                      {(Array.isArray(content.indicazioni) ? content.indicazioni.length : 0) < 4 && (
                        <button type="button" data-testid="add-indicazione" onClick={() => addItem("indicazioni", { servizio: "Personal Training", livello: "fortemente_consigliato", frequenza: "", motivo: "" }, 4)} className="text-xs text-primary hover:underline">+ Aggiungi indicazione</button>
                      )}
                    </div>
                    {(Array.isArray(content.indicazioni) ? content.indicazioni : []).map((x, i) => (
                      <div key={i} className="mt-2 rounded-xl border border-border/60 p-3" data-testid={`indicazione-${i}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <select value={x.servizio || ""} onChange={(e) => setArr("indicazioni", i, "servizio", e.target.value)} data-testid={`indicazione-${i}-servizio`} className="h-9 rounded-xl border border-border bg-background px-2 text-sm">
                            <option value="">—</option>
                            {PERCORSO_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <select value={x.livello || "fortemente_consigliato"} onChange={(e) => setArr("indicazioni", i, "livello", e.target.value)} data-testid={`indicazione-${i}-livello`} className="h-9 rounded-xl border border-border bg-background px-2 text-sm">
                            {LIVELLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          <select value={x.ruolo || ""} onChange={(e) => setArr("indicazioni", i, "ruolo", e.target.value)} data-testid={`indicazione-${i}-ruolo`} className="h-9 rounded-xl border border-border bg-background px-2 text-sm">
                            {RUOLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          <button type="button" onClick={() => removeItem("indicazioni", i)} className="text-destructive text-xs ml-auto" data-testid={`indicazione-${i}-remove`}>✕</button>
                        </div>
                        <Input value={x.frequenza || ""} onChange={(e) => setArr("indicazioni", i, "frequenza", e.target.value)} className="mt-2 rounded-xl h-9" placeholder="Frequenza / modalità (es. 2 sessioni a settimana)" data-testid={`indicazione-${i}-frequenza`} />
                        <Textarea value={x.motivo || ""} onChange={(e) => setArr("indicazioni", i, "motivo", e.target.value)} className="mt-2 rounded-xl min-h-[44px]" placeholder="Motivazione" />
                      </div>
                    ))}

                    <div className="mt-5 flex items-center justify-between">
                      <p className="fs-eyebrow text-[10px]">Dalle tue analisi emerge (max 4)</p>
                      {(Array.isArray(content.analisi_sangue) ? content.analisi_sangue.length : 0) < 4 && (
                        <button type="button" data-testid="add-analisi" onClick={() => addItem("analisi_sangue", { stato: "verde", testo: "" }, 4)} className="text-xs text-primary hover:underline">+ Aggiungi voce</button>
                      )}
                    </div>
                    {(Array.isArray(content.analisi_sangue) ? content.analisi_sangue : []).map((it, i) => (
                      <div key={i} className="mt-2 rounded-xl border border-border/60 p-3 flex items-start gap-2" data-testid={`analisi-item-${i}`}>
                        <select value={it.stato || "verde"} onChange={(e) => setArr("analisi_sangue", i, "stato", e.target.value)} data-testid={`analisi-${i}-stato`} className="text-xs rounded-md border border-border px-2 py-1 bg-background mt-1">
                          {BLOOD_STATI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <Textarea value={it.testo || ""} onChange={(e) => setArr("analisi_sangue", i, "testo", e.target.value)} className="flex-1 rounded-xl min-h-[38px]" placeholder="Voce analisi" />
                        <button type="button" onClick={() => removeItem("analisi_sangue", i)} className="text-destructive text-xs mt-1" data-testid={`analisi-${i}-remove`}>✕</button>
                      </div>
                    ))}

                    <div className="mt-5">
                      <Label className="text-xs font-semibold">Il tuo prossimo passo</Label>
                      <Textarea data-testid="field-prossimo" value={content.prossimo_passo || ""} onChange={(e) => setField("prossimo_passo", e.target.value)} className="mt-1 rounded-xl min-h-[60px]" />
                    </div>

                    <div className="mt-4">
                      <Label className="text-xs font-semibold">Nota interna staff</Label>
                      <Textarea data-testid="field-warning-staff" value={content.warning_staff || ""} onChange={(e) => setField("warning_staff", e.target.value)} className="mt-1 rounded-xl min-h-[44px]" placeholder="Nota di servizio per lo staff" />
                    </div>
                  </>
                )}

                <Button onClick={saveManual} disabled={!!busy} data-testid="save-report" className="mt-4 h-11 rounded-full">Salva modifiche</Button>
                <div className="mt-5">
                  <Label className="text-xs font-semibold">Indicazioni dello staff / Modifiche al report</Label>
                  <Textarea data-testid="report-instructions" value={instr} onChange={(e) => setInstr(e.target.value)} className="mt-1 rounded-xl min-h-[80px]" placeholder="Es. 'Dai priorità alla Nutrizione, accorcia il punto di partenza, evidenzia il sonno.'" />
                  <Button onClick={applyChanges} disabled={!!busy || !instr.trim()} data-testid="apply-changes" className="mt-2 h-11 rounded-full">{busy === "apply" ? "Applico…" : "Applica modifiche al report"}</Button>
                </div>
              </div>
            )}
          </section>

        </motion.div>
      </main>
    </div>
  );
}
